/**
 * Partner link API service
 *
 * Public endpoints behind the page a partner's link opens (no Orchard login).
 * Redeeming the link token sets an HttpOnly session cookie scoped to one
 * property, so every call includes credentials. The cookie lasts 30 minutes;
 * `withSession` renews it from the token held in memory when a call answers 401.
 */

import { z } from 'zod'
import { config } from '@/config'

const API_BASE_URL = config.apiUrl

// =============================================================================
// Types
// =============================================================================

/** What a link may do. The redeem and session responses list the ones it carries. */
export const PARTNER_CAPABILITIES = {
  offerCreate: 'offer:create',
  offerViewOwn: 'offer:view_own',
  offerWithdraw: 'offer:withdraw',
  offerNegotiate: 'offer:negotiate',
  propertyRequestInfo: 'property:request_info',
} as const

export type PartnerCapability = (typeof PARTNER_CAPABILITIES)[keyof typeof PARTNER_CAPABILITIES]

const partnerSummarySchema = z.object({
  slug: z.string(),
  display_name: z.string(),
})

/** Redemption and the session read answer with the same shape. */
const sessionResponseSchema = z.object({
  partner: partnerSummarySchema,
  property_id: z.string(),
  capabilities: z.array(z.string()),
})

/** Matches ConditionType on the marketplace side. */
const conditionRating = z.enum(['excellent', 'good', 'fair', 'poor', 'unknown']).nullable()

const partnerPhotoSchema = z.object({ url: z.string(), thumbnail_url: z.string().nullable() })

const partnerMediaSchema = partnerPhotoSchema.extend({ media_type: z.enum(['photo', 'video']) })

/**
 * The partner property read.
 *
 * Fields with defaults may be missing from a response sent by an API that
 * predates them; `media` then falls back to the photos.
 */
const partnerPropertySchema = z
  .object({
    id: z.string(),
    status: z.string(),
    submitted_at: z.string().nullable().default(null),
    address: z
      .object({
        street_address: z.string(),
        unit_number: z.string().nullable(),
        city: z.string(),
        state: z.string(),
        zipcode: z.string(),
      })
      .nullable(),
    property_type: z.string(),
    beds: z.number().nullable(),
    baths: z.number().nullable(),
    sqft: z.number().nullable(),
    year_built: z.number().nullable(),
    closing_timeline: z.string().nullable(),
    offer_deadline: z.string().nullable(),
    additional_notes: z.string().nullable(),
    market_value: z.number().nullable(),
    floor_price: z.number().nullable(),
    house_canary_valuation: z.number().nullable().default(null),
    /** Ready photos only, in display order. */
    photos: z.array(partnerPhotoSchema),
    /** Ready photos and videos, in display order. */
    media: z.array(partnerMediaSchema).optional(),
    // Condition as the listing agent recorded it — the same six ratings and
    // notes a signed-in investor sees.
    overall_condition: conditionRating,
    overall_condition_notes: z.string().nullable(),
    hvac_condition: conditionRating,
    hvac_condition_notes: z.string().nullable(),
    roof_condition: conditionRating,
    roof_condition_notes: z.string().nullable(),
    plumbing_condition: conditionRating,
    plumbing_condition_notes: z.string().nullable(),
    electrical_condition: conditionRating,
    electrical_condition_notes: z.string().nullable(),
    foundation_condition: conditionRating,
    foundation_condition_notes: z.string().nullable(),
    hvac_install_year: z.number().nullable().default(null),
    hvac_install_year_unknown: z.boolean().default(false),
    roof_replacement_year: z.number().nullable().default(null),
    roof_replacement_year_unknown: z.boolean().default(false),
    water_heater_install_year: z.number().nullable().default(null),
    water_heater_install_year_unknown: z.boolean().default(false),
  })
  .transform((property) => ({
    ...property,
    media: property.media ?? property.photos.map((photo) => ({ ...photo, media_type: 'photo' as const })),
  }))

const closingTimelineSchema = z.enum(['10_days', '14_days', '30_days', 'flexible']).nullable()

const partnerNegotiationSchema = z.object({
  id: z.number(),
  role: z.enum(['investor', 'agent']),
  offer_price: z.number(),
  earnest_money_deposit: z.number().nullable(),
  closing_timeline: closingTimelineSchema,
  notes: z.string().nullable(),
  created_at: z.string(),
})

const partnerOfferSchema = z.object({
  id: z.string(),
  status: z.enum(['pending', 'engaged', 'accepted', 'rejected', 'withdrawn']),
  offer_price: z.number(),
  earnest_money_deposit: z.number().nullable(),
  closing_timeline: closingTimelineSchema,
  notes: z.string().nullable(),
  private_notes: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  /** Oldest first. */
  negotiations: z.array(partnerNegotiationSchema),
})

const partnerQuestionSchema = z.object({
  id: z.string(),
  created_at: z.string(),
  /** Oldest first; the first entry is the question itself. */
  entries: z.array(
    z.object({
      id: z.string(),
      sender: z.enum(['investor', 'agent']),
      message: z.string(),
      created_at: z.string(),
    })
  ),
})

const ownOfferResponseSchema = z.object({ offer: partnerOfferSchema.nullable() })
const questionsResponseSchema = z.object({ questions: z.array(partnerQuestionSchema) })

export type PartnerSummary = z.infer<typeof partnerSummarySchema>
export type PartnerSession = z.infer<typeof sessionResponseSchema>
export type PartnerProperty = z.infer<typeof partnerPropertySchema>
export type PartnerNegotiation = z.infer<typeof partnerNegotiationSchema>
export type PartnerOffer = z.infer<typeof partnerOfferSchema>
export type PartnerQuestion = z.infer<typeof partnerQuestionSchema>

/** Body of an offer from a link: the investor offer fields without the property ID. */
export interface PartnerOfferCreate {
  offer_price: number
  earnest_money_deposit?: number
  closing_timeline?: NonNullable<PartnerOffer['closing_timeline']>
  notes?: string
  private_notes?: string
}

/** Body of a counter offer: the product's negotiation fields. */
export interface PartnerCounterOfferCreate {
  offer_price: number
  earnest_money_deposit?: number
  closing_timeline?: NonNullable<PartnerOffer['closing_timeline']>
  notes?: string
}

/**
 * Why a partner link could not be shown:
 * - `invalid`: the link is tampered, revoked or unknown, or its session is gone.
 * - `unavailable`: the property no longer takes offers.
 * - `temporary`: the network or the service failed; trying again may work.
 */
export type PartnerLinkFailure = 'invalid' | 'unavailable' | 'temporary'

const validationIssueSchema = z.object({
  loc: z.array(z.union([z.string(), z.number()])),
  msg: z.string(),
})

/** One entry of a 422 response's field list. */
export type PartnerValidationIssue = z.infer<typeof validationIssueSchema>

/** The `detail` of an error response: a sentence, or the 422 field list. */
export type PartnerErrorDetail = string | PartnerValidationIssue[]

const errorBodySchema = z.object({
  detail: z.union([z.string(), z.array(validationIssueSchema)]),
})

interface PartnerLinkErrorOptions {
  /** HTTP status of the response; absent when no response arrived or it could not be read. */
  status?: number
  detail?: PartnerErrorDetail
  /** The underlying error, when there was one. */
  cause?: unknown
}

export class PartnerLinkError extends Error {
  readonly failure: PartnerLinkFailure
  readonly status: number | undefined
  readonly detail: PartnerErrorDetail | undefined
  /** The underlying error, when there was one. */
  readonly details?: unknown

  constructor(failure: PartnerLinkFailure, options: PartnerLinkErrorOptions = {}) {
    super(`Partner link failed: ${failure}`)
    this.name = 'PartnerLinkError'
    this.failure = failure
    this.status = options.status
    this.detail = options.detail
    this.details = options.cause
  }
}

// =============================================================================
// Request plumbing
// =============================================================================

function failureForStatus(status: number): PartnerLinkFailure {
  if (status === 410) return 'unavailable'
  if (status === 400 || status === 401 || status === 403 || status === 422) return 'invalid'
  return 'temporary'
}

/** Read `detail` from an error response; undefined when the body has none. */
async function readErrorDetail(response: Response): Promise<PartnerErrorDetail | undefined> {
  let body: unknown
  try {
    body = await response.json()
  } catch {
    // An error page or an empty body: the status alone has to do.
    return undefined
  }
  const parsed = errorBodySchema.safeParse(body)
  return parsed.success ? parsed.data.detail : undefined
}

async function request<T>(path: string, init: RequestInit, schema: z.ZodType<T>): Promise<T> {
  let response: Response
  try {
    response = await fetch(`${API_BASE_URL}${path}`, { ...init, credentials: 'include' })
  } catch (error) {
    throw new PartnerLinkError('temporary', { cause: error })
  }

  if (!response.ok) {
    throw new PartnerLinkError(failureForStatus(response.status), {
      status: response.status,
      detail: await readErrorDetail(response),
    })
  }

  let body: unknown
  try {
    body = await response.json()
  } catch (error) {
    throw new PartnerLinkError('temporary', { status: response.status, cause: error })
  }

  const parsed = schema.safeParse(body)
  if (!parsed.success) {
    throw new PartnerLinkError('temporary', { status: response.status, cause: parsed.error })
  }
  return parsed.data
}

function jsonInit(method: 'POST' | 'PATCH', body?: unknown): RequestInit {
  return body === undefined
    ? { method }
    : { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
}

/**
 * Run a call that needs the session cookie, renewing the session once if it expired.
 *
 * Only a 401 renews it, and only when the link token is still held in memory:
 * a page reloaded without its token has nothing to renew with, so its 401 is
 * final. The call is retried once after a successful redemption. When the
 * redemption itself fails (410: the property is gone; 400: the link is no
 * longer valid), that failure is thrown as is and the call is not retried, so
 * the caller can treat it like any other refusal and reload the page.
 */
export async function withSession<T>(token: string, call: () => Promise<T>): Promise<T> {
  try {
    return await call()
  } catch (error) {
    if (!(error instanceof PartnerLinkError) || error.status !== 401 || token === '') {
      throw error
    }
  }
  await redeemPartnerLink(token)
  return call()
}

// =============================================================================
// Session and reads
// =============================================================================

/**
 * Exchange a partner link token for a session scoped to its one property.
 */
export function redeemPartnerLink(token: string): Promise<PartnerSession> {
  return request('/partner/links/redeem', jsonInit('POST', { token }), sessionResponseSchema)
}

/**
 * Read the session the cookie carries, for a page reloaded without its token.
 */
export function getPartnerSession(): Promise<PartnerSession> {
  return request('/partner/session', { method: 'GET' }, sessionResponseSchema)
}

/**
 * Get the property a redeemed partner link is bound to.
 */
export function getPartnerProperty(propertyId: string): Promise<PartnerProperty> {
  return request(
    `/partner/properties/${encodeURIComponent(propertyId)}`,
    { method: 'GET' },
    partnerPropertySchema
  )
}

/**
 * The link subject's newest offer on the property, in any status, or null.
 */
export async function getPartnerOffer(propertyId: string): Promise<PartnerOffer | null> {
  const { offer } = await request(
    `/partner/properties/${encodeURIComponent(propertyId)}/offer`,
    { method: 'GET' },
    ownOfferResponseSchema
  )
  return offer
}

/**
 * The link subject's questions on the property, newest first.
 */
export async function getPartnerQuestions(propertyId: string): Promise<PartnerQuestion[]> {
  const { questions } = await request(
    `/partner/properties/${encodeURIComponent(propertyId)}/questions`,
    { method: 'GET' },
    questionsResponseSchema
  )
  return questions
}

// =============================================================================
// Mutations — each renews an expired session through withSession
// =============================================================================

export function createPartnerOffer(
  token: string,
  propertyId: string,
  body: PartnerOfferCreate
): Promise<PartnerOffer> {
  return withSession(token, () =>
    request(
      `/partner/properties/${encodeURIComponent(propertyId)}/offers`,
      jsonInit('POST', body),
      partnerOfferSchema
    )
  )
}

export function withdrawPartnerOffer(token: string, offerId: string): Promise<PartnerOffer> {
  return withSession(token, () =>
    request(`/partner/offers/${encodeURIComponent(offerId)}/withdraw`, jsonInit('PATCH'), partnerOfferSchema)
  )
}

export function counterPartnerOffer(
  token: string,
  offerId: string,
  body: PartnerCounterOfferCreate
): Promise<PartnerNegotiation> {
  return withSession(token, () =>
    request(
      `/partner/offers/${encodeURIComponent(offerId)}/negotiations`,
      jsonInit('POST', body),
      partnerNegotiationSchema
    )
  )
}

export function askPartnerQuestion(
  token: string,
  propertyId: string,
  message: string
): Promise<PartnerQuestion> {
  return withSession(token, () =>
    request(
      `/partner/properties/${encodeURIComponent(propertyId)}/questions`,
      jsonInit('POST', { message }),
      partnerQuestionSchema
    )
  )
}

export function followUpPartnerQuestion(
  token: string,
  questionId: string,
  message: string
): Promise<PartnerQuestion> {
  return withSession(token, () =>
    request(
      `/partner/questions/${encodeURIComponent(questionId)}/messages`,
      jsonInit('POST', { message }),
      partnerQuestionSchema
    )
  )
}
